/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { SpouseType } from '../types/on-boarding.type';
import {
  useCreateSpouse,
  useDeleteSpouses,
  useGetSpousesList
} from 'common/queries-and-mutations/spouse';
import { Loader } from 'components/Loader';
import {
  findNextPage,
  findPreviousPage,
  hasEmptyValue,
  showRemoveButton
} from 'utils';
import { useUpdateStep } from 'common/queries-and-mutations/user';
import { STEPS } from 'constants/steps';
import GenderField from 'components/Form/GenderField';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { fetchUserInformation } from 'thunks/account-thunk';
import InfoBar from './InfoBar';

export default function SpouseInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError } = useCreateSpouse();
  const { mutate: deleteSpouse, isLoading: isDeleting } = useDeleteSpouses();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    mutate: updateStep,
    isSuccess: stepSuccess,
    isError: stepError
  } = useUpdateStep();

  const {
    data,
    isSuccess: isSpouseSuccess,
    isLoading: isSpouseLoading
  } = useGetSpousesList();

  const spouse = useMemo(() => {
    if (isSpouseSuccess && data.length > 0) {
      return data.map(spouse => ({
        spouseId: spouse.id,
        spouseFirstName: spouse.spouseFirstName,
        spouseMiddleName: spouse.spouseMiddleName,
        spouseLastName: spouse.spouseLastName,
        homeAddress: spouse.homeAddress,
        emailAddress: spouse.emailAddress,
        phoneNumber: spouse.phoneNumber,
        gender: spouse.gender
      }));
    } else {
      return [
        {
          spouseId: null,
          spouseFirstName: '',
          spouseMiddleName: '',
          spouseLastName: '',
          homeAddress: '',
          emailAddress: '',
          phoneNumber: '',
          gender: ''
        }
      ];
    }
  }, [data, isSpouseSuccess]);

  const formik = useFormik({
    initialValues: {
      spouse
    },
    // validationSchema: Yup.object({
    //   spouse: Yup.array().of(
    //     Yup.object().shape({
    //       spouseName: Yup.string().required('Spouse name is required').nullable(),
    //       homeAddress: Yup.string().required('Home address is required').nullable(),
    //       emailAddress: Yup.string()
    //         .email('Invalid email')
    //         .required('Email is required').nullable(),
    //       phoneNumber: Yup.string().required('Phone number is required').nullable()
    //     })
    //   )
    // }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      const excludedFields = ['spouseId', 'spouseMiddleName']; // Fields to exclude from validation
      if (hasEmptyValue(values.spouse, excludedFields)) {
        updateStep(STEPS.SPOUSE_INFORMATION);
      } else {
        onSpouseSubmit(values);
      }
    }
  });

  const addMoreSpouse = () => {
    formik.values.spouse.push({
      spouseId: null,
      spouseFirstName: '',
      spouseMiddleName: '',
      spouseLastName: '',
      homeAddress: '',
      emailAddress: '',
      phoneNumber: '',
      gender: ''
    });
    formik.setFieldValue('spouse', [...formik.values.spouse]);
  };

  const removeSpouse = (indexToRemove: number, spouseId?: number | null) => {
    if (spouseId) {
      deleteSpouse(spouseId);
    }
    formik.values.spouse.splice(indexToRemove, 1);
    formik.setFieldValue('spouse', [...formik.values.spouse]);
  };

  const onSpouseSubmit = (formValue: SpouseType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (stepSuccess && !stepError) {
      setIsSubmitting(false);
      navigate(findNextPage(planAllowPage, STEPS.SPOUSE_INFORMATION));
      dispatch(fetchUserInformation());
    }
  }, [stepSuccess, stepError]);

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.SPOUSE_INFORMATION));
    } else if (isError) {
      setIsSubmitting(false);
    }
  }, [dispatch, isError, isSuccess, navigate, planAllowPage]);

  const onContinueLater = () => {
    setIsLater(true);
    const excludedFields = ['spouseId', 'spouseMiddleName']; // Fields to exclude from validation
    if (hasEmptyValue(formik.values.spouse, excludedFields)) {
      updateStep(STEPS.SPOUSE_INFORMATION);
    } else {
      mutate(formik.values);
    }
  };

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.SPOUSE_INFORMATION));
  };

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={3}
        stepPercentage={STEPS.SPOUSE_INFORMATION}
        timelineStep={1}
      />

      {isSpouseLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
            Spouse Information
          </p>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.spouse.map((spouse, index) => (
              <div key={index} className="my-8">
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`spouse[${index}].spouseFirstName`}
                      placeholder=""
                      value={spouse.spouseFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Spouse First Name"
                      error={
                        formik.touched.spouse
                          ? formik.errors.spouse?.[index] &&
                            formik.errors.spouse.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.spouse[index])
                              ).spouseFirstName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`spouse[${index}].spouseMiddleName`}
                      placeholder=""
                      value={spouse.spouseMiddleName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Spouse Middle Name"
                      error={
                        formik.touched.spouse
                          ? formik.errors.spouse?.[index] &&
                            formik.errors.spouse.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.spouse[index])
                              ).spouseMiddleName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`spouse[${index}].spouseLastName`}
                      placeholder=""
                      value={spouse.spouseLastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Spouse Last Name"
                      error={
                        formik.touched.spouse
                          ? formik.errors.spouse?.[index] &&
                            formik.errors.spouse.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.spouse[index])
                              ).spouseLastName
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`spouse[${index}].homeAddress`}
                      placeholder=""
                      value={spouse.homeAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Home Address"
                      error={
                        formik.touched.spouse
                          ? formik.errors.spouse?.[index] &&
                            formik.errors.spouse.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.spouse[index])
                              ).homeAddress
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`spouse[${index}].emailAddress`}
                      placeholder=""
                      value={spouse.emailAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Email Address"
                      error={
                        formik.touched.spouse
                          ? formik.errors.spouse?.[index] &&
                            formik.errors.spouse.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.spouse[index])
                              ).emailAddress
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`spouse[${index}].phoneNumber`}
                      placeholder=""
                      value={spouse.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Phone Number"
                      error={
                        formik.touched.spouse
                          ? formik.errors.spouse?.[index] &&
                            formik.errors.spouse.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.spouse[index])
                              ).phoneNumber
                            : ''
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <GenderField
                      gender={spouse.gender}
                      onSelect={gender =>
                        formik.setFieldValue(`spouse[${index}].gender`, gender)
                      }
                    />
                  </div>
                </div>
                {showRemoveButton(index, spouse.spouseId, data!.length > 1) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeSpouse(index, spouse.spouseId)}
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              <div
                className="flex items-center gap-4"
                onClick={addMoreSpouse}
                role="button"
                tabIndex={-1}
              >
                <Icon name={Icons.AddCircleOutline} />
                <span className="mt-2 text-base font-medium text-[#353535]">
                  Add Another Spouse
                </span>
              </div>
            </div>

            <div className="flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-10 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={onContinueLater}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="md"
                  className="mt-5 w-[9rem] rounded p-[0.1rem]"
                  label="Proceed"
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
